import React, { useEffect, useMemo, useState } from 'react';

import SearchIcon from '@mui/icons-material/Search';
import {
  FormHelperText,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
} from '@mui/material';

import { useDashboardTheme } from '@/pages/MeetingDashboard/themeHelper';

type OptionType = {
  label: string;
  value: string;
};

type DropdownProps = {
  label: string;
  options?: OptionType[];
  value: string;
  onChange?: (e: SelectChangeEvent<string>) => void;
  className?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  showClearOption?: boolean;
  required?: boolean;
};

const DropdownMain: React.FC<DropdownProps> = ({
  label,
  options,
  value,
  onChange,
  className = '',
  error = false,
  helperText = '',
  disabled = false,
  showClearOption = true,
  required = false,
}) => {
  const { isDark } = useDashboardTheme();
  const isDarkMode = isDark;

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedTerm(searchTerm);
    }, 300);
    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const filteredOptions = useMemo(() => {
    if (!options) return [];
    if (!debouncedTerm) return options;
    return options.filter((opt) => opt.label.toLowerCase().includes(debouncedTerm.toLowerCase()));
  }, [options, debouncedTerm]);

  return (
    <div className={`mb-1 ${className}`}>
      <label
        className={`block text-sm my-1 pt-2 ${
          disabled ? 'text-gray-400' : isDarkMode ? 'text-white' : 'text-purple-950 font-bold'
        }`}
      >
        {label}
        {required && <span style={{ color: '#ef4444' }}> *</span>}
      </label>

      <div className="relative w-full min-w-0">
        <Select
          id={`${label}-select`}
          value={value}
          onChange={onChange}
          disabled={disabled}
          displayEmpty
          onClose={() => setSearchTerm('')}
          renderValue={(selected) => {
            if (!selected) {
              return (
                <span className={isDarkMode ? 'text-gray-400' : 'text-slate-600 font-medium'}>
                  Select...
                </span>
              );
            }
            const selectedLabel = options?.find((o) => o.value === selected)?.label || selected;
            return (
              <span
                className={`text-xs px-3 py-1 rounded-full inline-flex items-center gap-1 font-semibold ${
                  isDarkMode
                    ? 'bg-green text-black'
                    : 'bg-purple-100 border border-purple-200 text-purple-900'
                }`}
                style={{ backgroundColor: isDarkMode ? '#B8F890' : undefined }}
              >
                {selectedLabel}
                <span
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    onChange?.({ target: { value: '' } } as SelectChangeEvent<string>);
                  }}
                  className="ml-1 cursor-pointer hover:opacity-70"
                >
                  ✕
                </span>
              </span>
            );
          }}
          sx={{
            width: '100%',
            backgroundColor: isDarkMode ? 'var(--color-surface)' : '#ffffff',
            color: isDarkMode ? 'white' : '#0f172a',
            borderRadius: '12px',
            minHeight: '44px',
            height: 'auto',
            marginTop: '8px',
            border: isDarkMode ? '1px solid var(--color-border)' : '1px solid #cbd5e1',
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&.Mui-focused': {
              borderColor: isDarkMode ? '#B8F890' : '#7C3AED',
            },
            '&:hover': {
              borderColor: isDarkMode ? '#B8F890' : '#7C3AED',
            },
            '& .MuiSelect-icon': {
              color: isDarkMode ? '#B8F890' : '#7C3AED',
            },
            '& .MuiSelect-select': {
              paddingTop: '8px',
              paddingBottom: '8px',
            },
            ...(error && {
              border: '2px solid #f44336',
            }),
            ...(disabled && {
              opacity: 0.5,
            }),
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: isDarkMode ? 'var(--color-surface)' : '#ffffff',
                color: isDarkMode ? 'white' : '#0f172a',
                maxHeight: 300,
                borderRadius: '8px',
                border: isDarkMode ? '1px solid var(--color-border)' : '1px solid #e2e8f0',
                boxShadow: isDarkMode
                  ? 'none'
                  : '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
              },
            },
          }}
        >
          <ListSubheader
            sx={{
              p: 1,
              backgroundColor: isDarkMode ? 'var(--color-surface)' : '#ffffff',
            }}
          >
            <TextField
              size="small"
              autoFocus
              placeholder="Search..."
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Escape') {
                  e.stopPropagation();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      fontSize="small"
                      sx={{ color: isDarkMode ? '#B8F890' : '#7C3AED' }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: isDarkMode ? 'white' : 'black',
                  backgroundColor: isDarkMode ? 'var(--color-bg)' : '#f8fafc',
                  '& fieldset': {
                    borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                  },
                  '&:hover fieldset': {
                    borderColor: isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: isDarkMode ? '#B8F890' : '#7C3AED',
                  },
                },
              }}
            />
          </ListSubheader>

          {showClearOption && (
            <MenuItem
              value=""
              sx={{
                backgroundColor: isDarkMode ? 'var(--color-surface)' : '#ffffff',
                color: isDarkMode ? 'gray' : '#64748b',
                fontStyle: 'italic',
                '&:hover': { backgroundColor: isDarkMode ? 'var(--color-bg)' : '#f1f5f9' },
              }}
            >
              Clear
            </MenuItem>
          )}

          {!filteredOptions || filteredOptions.length === 0 ? (
            <MenuItem
              disabled
              sx={{
                backgroundColor: isDarkMode ? 'var(--color-surface)' : '#ffffff',
                color: isDarkMode ? 'gray' : '#94a3b8',
                fontStyle: 'italic',
              }}
            >
              No Options Available
            </MenuItem>
          ) : (
            filteredOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={disabled}
                sx={{
                  backgroundColor: isDarkMode ? 'var(--color-surface)' : '#ffffff',
                  color: isDarkMode ? 'white' : '#0f172a',
                  '&.Mui-selected': { backgroundColor: isDarkMode ? 'var(--color-bg)' : '#f1f5f9' },
                  '&:hover': { backgroundColor: isDarkMode ? 'var(--color-bg)' : '#f8fafc' },
                }}
              >
                {option.label}
              </MenuItem>
            ))
          )}
        </Select>

        {error && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500">⚠</span>}
      </div>

      {error && helperText && (
        <FormHelperText sx={{ color: '#f44336', marginLeft: 1 }}>{helperText}</FormHelperText>
      )}
    </div>
  );
};

export default DropdownMain;
